import type { ReactNode } from 'react'
import { cn } from './cn'
import { humanize } from '@/lib/format'

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'primary' | 'accent'

const tones: Record<Tone, { pill: string; dot: string }> = {
  neutral: { pill: 'bg-neutral-soft text-neutral-strong', dot: 'bg-text-subtle' },
  success: { pill: 'bg-success-soft text-success-strong', dot: 'bg-success' },
  warning: { pill: 'bg-warning-soft text-warning-strong', dot: 'bg-warning' },
  danger: { pill: 'bg-danger-soft text-danger-strong', dot: 'bg-danger' },
  info: { pill: 'bg-info-soft text-info-strong', dot: 'bg-info' },
  primary: { pill: 'bg-primary-soft text-primary-strong', dot: 'bg-primary' },
  accent: { pill: 'bg-accent-soft text-accent-strong', dot: 'bg-accent' },
}

export interface BadgeProps {
  tone?: Tone
  /** Leading status dot (default true for StatusBadge). */
  dot?: boolean
  /** Pulsing dot for live/in-progress states. */
  pulse?: boolean
  icon?: ReactNode
  size?: 'sm' | 'md'
  className?: string
  children: ReactNode
  title?: string
}

/** Soft tinted pill. Colour only for meaning: `<Badge tone="warning">Low stock</Badge>`. */
export function Badge({ tone = 'neutral', dot, pulse, icon, size = 'md', className, children, title }: BadgeProps) {
  const t = tones[tone]
  return (
    <span title={title} className={cn(
      'inline-flex max-w-full shrink-0 items-center gap-1 rounded-full font-medium whitespace-nowrap [&_svg]:size-3',
      size === 'sm' ? 'h-5 px-2 text-xs' : 'h-6 px-2 text-sm',
      t.pill, className,
    )}>
      {dot && (
        <span className="relative flex size-1.5 shrink-0">
          {pulse && <span className={cn('absolute inset-0 animate-ping rounded-full opacity-60', t.dot)} />}
          <span className={cn('relative size-1.5 rounded-full', t.dot)} />
        </span>
      )}
      {icon}
      <span className="truncate">{children}</span>
    </span>
  )
}

/**
 * Status vocabulary → tone. Covers every status in shared/types.ts (orders, payments, fulfillment,
 * products, checkouts, domains, reviews, jobs, stores, subscriptions). Unknown words are neutral.
 */
export const STATUS_TONES: Record<string, Tone> = {
  // financial / payments
  paid: 'success', succeeded: 'success', partially_paid: 'warning', pending: 'warning', authorized: 'info',
  partially_refunded: 'neutral', refunded: 'neutral', voided: 'neutral', failed: 'danger', mismatch: 'danger', cancelled: 'danger', canceled: 'danger',
  // fulfillment
  unfulfilled: 'warning', partial: 'warning', fulfilled: 'success', shipped: 'info', in_transit: 'info', delivered: 'success', returned: 'neutral', rto: 'danger',
  // orders / checkouts
  open: 'info', closed: 'neutral', processing: 'info', completed: 'success', expired: 'neutral', recovered: 'success', abandoned: 'warning',
  // catalog / content
  draft: 'neutral', active: 'success', archived: 'neutral', published: 'success', hidden: 'neutral', scheduled: 'info', rejected: 'danger',
  // domains / jobs / stores / billing
  pending_dns: 'warning', provisioning: 'info', removing: 'neutral', verified: 'success',
  queued: 'neutral', running: 'info', done: 'success', dead: 'danger',
  under_review: 'warning', suspended: 'danger', past_due: 'danger', trialing: 'info',
  // COD
  confirmed: 'success', declined: 'danger',
  // risk / reports
  resolved: 'success', dismissed: 'neutral', low: 'neutral', medium: 'warning', high: 'danger', critical: 'danger',
}

/** Friendlier labels where humanize() isn't enough. */
const STATUS_LABELS: Record<string, string> = {
  pending_dns: 'Waiting for DNS', partially_paid: 'Partially paid', partially_refunded: 'Partially refunded',
  unfulfilled: 'Unfulfilled', partial: 'Partially fulfilled', rto: 'Returned to origin', past_due: 'Past due', under_review: 'Under review',
}

const LIVE = new Set(['processing', 'running', 'provisioning', 'pending_dns', 'in_transit'])

/**
 * `<StatusBadge status={order.financialStatus} />` → "Paid" (green). Override with `label`/`tone`.
 */
export function StatusBadge({ status, label, tone, size, className }: { status: string | null | undefined; label?: ReactNode; tone?: Tone; size?: 'sm' | 'md'; className?: string }) {
  if (!status) return null
  const key = status.toLowerCase()
  return (
    <Badge tone={tone ?? STATUS_TONES[key] ?? 'neutral'} dot pulse={LIVE.has(key)} size={size} className={className}>
      {label ?? STATUS_LABELS[key] ?? humanize(key)}
    </Badge>
  )
}
